import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Box, Container, Card, CardContent, Typography, Button, TextField,
    Select, MenuItem, InputLabel, FormControl, LinearProgress, Grid,
    CircularProgress, Modal, IconButton, Alert
} from '@mui/material';
import {
    CloudUpload, Delete, ArrowBack, ArrowForward, CheckCircle, Edit
} from '@mui/icons-material';
import axios from 'axios';
import { useTemplate } from '../contexts/TemplateContext';

// 🔴 BUG FIX: Smart checkers to accurately identify field types
const isDateField = (field) => {
    const type = (field?.fieldType || field?.field_type || '').toLowerCase();
    if (type === 'date') return true;
    
    const name = (field?.field_name || field?.key || '').toLowerCase();
    const words = name.split(/[\s_-]+/); // Split by space, underscore, or dash
    return words.includes('date') || words.includes('dob');
};

const isTimeField = (field) => {
    const type = (field?.fieldType || field?.field_type || '').toLowerCase();
    if (type === 'time') return true;
    
    const name = (field?.field_name || field?.key || '').toLowerCase();
    const words = name.split(/[\s_-]+/);
    return words.includes('time');
};

const SmartForm = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { template_id } = useParams();
    const { updateDesignEntry } = useTemplate();

    const [fields, setFields] = useState([]);
    const [template, setTemplate] = useState({});
    const [entry, setEntry] = useState({});
    const [formData, setFormData] = useState({});
    const [dateFields, setDateFields] = useState({});
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showReview, setShowReview] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadSuccessUrl, setUploadSuccessUrl] = useState('');

    useEffect(() => {
        const passedData = location.state?.entryData;

        if (passedData) {
            try {
                const { fields: fieldsJSON, template: templateJSON, entry: entryJSON } = passedData;

                setFields(fieldsJSON || []);
                setTemplate(templateJSON || {});
                setEntry(entryJSON || {});

                const initialData = {};
                const initialDates = {};

                (fieldsJSON || []).forEach(field => {
                    const fieldId = field.field_name || field.key || field.id;
                    const defaultValue = field.field_value || field.field_default || '';

                    initialData[fieldId] = defaultValue;

                    // 🔴 FIX APPLIED HERE
                    if (isDateField(field)) {
                        const parts = defaultValue.split(' ');
                        if (parts.length === 3) initialDates[fieldId] = { day: parts[0], month: parts[1], year: parts[2] };
                        else initialDates[fieldId] = { day: '', month: '', year: '' };
                    }
                });

                setFormData(initialData);
                setDateFields(initialDates);
                setLoading(false);
            } catch (e) {
                console.error("Data parse error:", e);
                setErrorMsg('डेटा पार्स करने में त्रुटि। / Error parsing data.');
                setLoading(false);
            }
        } else {
            setErrorMsg('कोई डेटा नहीं मिला। कृपया पिछले पृष्ठ से वापस आकर पुनः प्रयास करें।');
            setLoading(false);
        }
    }, [location.state]);

    const handleInputChange = (fieldId, value) => {
        setFormData(prev => ({ ...prev, [fieldId]: value }));
    };

    const handleDateChange = (fieldId, type, value) => {
        const updatedDate = { ...dateFields[fieldId], [type]: value };
        setDateFields(prev => ({ ...prev, [fieldId]: updatedDate }));
        const { day, month, year } = updatedDate;
        handleInputChange(fieldId, [day, month, year].filter(Boolean).join(' '));
    };

    const handleFileUpload = async (fieldId, file) => {
        if (!file) return;
        const uploadData = new FormData();
        uploadData.append('image', file);

        setUploadModalOpen(true);
        setUploadProgress(0);
        setUploadSuccessUrl('');

        try {
            const response = await axios.post('/api/upload-image', uploadData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percent);
                }
            });

            const serverUrl = response?.data?.data?.url;
            if (serverUrl) {
                setUploadSuccessUrl(serverUrl);
                handleInputChange(fieldId, serverUrl);
            }
        } catch (error) {
            console.error("Upload error:", error);
            alert("Failed to upload image. / इमेज अपलोड करने में विफल।");
            setUploadModalOpen(false);
        }
    };

    const handleNext = () => {
        if (currentStep < fields.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            setShowReview(true);
        }
    };

    const handleBack = () => {
        if (showReview) {
            setShowReview(false);
        } else if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const submitForm = async () => {
        const entryId = entry?.id;
        if (!entryId) {
            alert('Entry ID is missing. Please restart the process.');
            return;
        }

        setIsSubmitting(true);

        const fieldValues = fields.map(field => {
            const fieldId = field.field_name || field.key || field.id;
            return {
                field_name: field.field_name || field.key,
                field_value: formData[fieldId],
                id: field.id || field.field_id || null,
                field_ref_id: field.global_field_id || field.id || null
            };
        });

        const res = await updateDesignEntry(entryId, { field_values: fieldValues });

        if (res.success) {
            const redirectUrl = res.data?.redirect_url || '/download';
            window.location.href = redirectUrl;
        } else {
            setIsSubmitting(false);
            if (res.status === 401 && res.redirectUrl) {
                navigate(res.redirectUrl);
            } else {
                alert('Error: ' + res.message);
            }
        }
    };

    const isCurrentFieldValid = () => {
        if (!fields.length) return true;
        const field = fields[currentStep];
        const fieldId = field.field_name || field.key || field.id;
        const isRequired = field.is_required || field.field_required;
        const val = formData[fieldId];

        if (isRequired && (!val || val.toString().trim() === '')) return false;

        const minLength = field.minLength || field.min_length || 0;
        const maxLength = field.maxLength || field.max_length || 255;

        if (val && typeof val === 'string' && !field.fieldType?.includes('image') && !fieldId.includes('photo')) {
            if (minLength > 0 && val.length < minLength) return false;
            if (maxLength > 0 && val.length > maxLength) return false;
        }

        return true;
    };

    if (loading) return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>फॉर्म लोड हो रहा है... / Loading form...</Typography>
        </Box>
    );

    if (errorMsg) return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>
            <Button variant="outlined" onClick={() => navigate(-1)} startIcon={<ArrowBack />}>Go Back</Button>
        </Container>
    );

    const currentField = fields[currentStep];
    const fieldId = currentField?.field_name || currentField?.key || currentField?.id;
    const fieldName = (currentField?.field_name || currentField?.key || '').toLowerCase();
    const isRequired = currentField?.is_required || currentField?.field_required;
    const labelEn = currentField?.labels?.en || currentField?.label || 'Field';
    const labelHi = currentField?.labels?.hi || '';

    const renderFieldInput = () => {
        // 1. DATE PICKER (🔴 FIX APPLIED HERE)
        if (isDateField(currentField)) {
            const dData = dateFields[fieldId] || { day: '', month: '', year: '' };
            return (
                <Grid container spacing={2}>
                    <Grid item xs={4}><FormControl fullWidth size="medium"><InputLabel>Day</InputLabel><Select value={dData.day} label="Day" onChange={(e) => handleDateChange(fieldId, 'day', e.target.value)}>{[...Array(31)].map((_, i) => <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>)}</Select></FormControl></Grid>
                    <Grid item xs={4}><FormControl fullWidth size="medium"><InputLabel>Month</InputLabel><Select value={dData.month} label="Month" onChange={(e) => handleDateChange(fieldId, 'month', e.target.value)}>{['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}</Select></FormControl></Grid>
                    <Grid item xs={4}><FormControl fullWidth size="medium"><InputLabel>Year</InputLabel><Select value={dData.year} label="Year" onChange={(e) => handleDateChange(fieldId, 'year', e.target.value)}>{[...Array(6)].map((_, i) => { const yr = new Date().getFullYear() + i; return <MenuItem key={yr} value={yr}>{yr}</MenuItem>; })}</Select></FormControl></Grid>
                    {formData[fieldId] && <Grid item xs={12}><Alert severity="success" sx={{ mt: 1 }}>📅 Selected: {formData[fieldId]}</Alert></Grid>}
                </Grid>
            );
        }

        // 2. TIME PICKER (🔴 FIX APPLIED HERE)
        if (isTimeField(currentField)) {
            const timeOptions = ['7am onwards', '8am onwards', '9am onwards', '10am onwards', '11am onwards', '12pm onwards', '1pm onwards', '2pm onwards', '3pm onwards', '4pm onwards', '5pm onwards', '6pm onwards', '7pm onwards', '8pm onwards', '9pm onwards', '10pm onwards', '11pm onwards'];
            return <Grid container spacing={2}>{timeOptions.map(time => <Grid item xs={6} sm={4} key={time}><Button fullWidth variant={formData[fieldId] === time ? "contained" : "outlined"} color={formData[fieldId] === time ? "success" : "inherit"} onClick={() => handleInputChange(fieldId, time)} sx={{ py: 1.5, textTransform: 'none', fontWeight: 'bold' }}>🕐 {time}</Button></Grid>)}</Grid>;
        }

        // 3. IMAGE UPLOAD
        if (currentField?.fieldType === 'image' || fieldName.includes('photo') || fieldName.includes('image') || fieldName.includes('logo')) {
            return <Box sx={{ textAlign: 'center' }}><input type="file" id={`file_${fieldId}`} style={{ display: 'none' }} accept="image/*" onChange={(e) => handleFileUpload(fieldId, e.target.files[0])} />{!formData[fieldId] ? <Box onClick={() => document.getElementById(`file_${fieldId}`).click()} sx={{ border: '2px dashed #ccc', borderRadius: 3, p: 4, cursor: 'pointer', '&:hover': { borderColor: 'primary.main', bgcolor: '#f0f7ff' } }}><CloudUpload color="primary" sx={{ fontSize: 50, mb: 1 }} /><Typography variant="h6">Click to Upload Photo</Typography></Box> : <Box sx={{ position: 'relative', display: 'inline-block', mt: 2 }}><img src={formData[fieldId]} alt="Uploaded" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', border: '1px solid #ddd' }} /><IconButton onClick={() => handleInputChange(fieldId, '')} sx={{ position: 'absolute', top: -15, right: -15, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}><Delete fontSize="small" /></IconButton></Box>}</Box>;
        }

        // 4. SELECT DROPDOWN
        if (currentField?.fieldType === 'select' && currentField?.field_options) {
            return <FormControl fullWidth><InputLabel>Select {labelEn}</InputLabel><Select value={formData[fieldId] || ''} onChange={(e) => handleInputChange(fieldId, e.target.value)} label={`Select ${labelEn}`}>{currentField.field_options.map((opt, i) => { const val = opt.option_value || opt.value || opt; const lab = opt.option_label || opt.label || opt; return <MenuItem key={i} value={val}>{lab}</MenuItem>; })}</Select></FormControl>;
        }

        // 5. REGULAR TEXT / TEXTAREA
        const isTextArea = currentField?.fieldType === 'textarea';
        // Check array types for numbers
        const inputType = (currentField?.fieldType === 'number' || fieldName.includes('mobile') || fieldName.includes('phone')) ? 'number' : 'text';
        
        return <TextField fullWidth type={inputType} multiline={isTextArea} rows={isTextArea ? 4 : 1} variant="outlined" placeholder={currentField?.sampleValues?.en || labelEn} value={formData[fieldId] || ''} onChange={(e) => handleInputChange(fieldId, e.target.value)} required={isRequired} autoFocus helperText={`${currentField?.sampleValues?.en ? `Ex: ${currentField?.sampleValues?.en}` : ''} ${currentField?.sampleValues?.hi ? `/ ${currentField?.sampleValues?.hi}` : ''}`} />;
    };

    return (
        <Container maxWidth="sm" sx={{ py: 3, minHeight: '100vh', bgcolor: '#f5f7fa' }}>
            <Card elevation={0} sx={{ mb: 3, borderRadius: 2, bgcolor: 'transparent' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Field {currentStep + 1} of {fields.length}</Typography>
                    <Typography variant="body2" color="primary" fontWeight="bold">{Math.round(((currentStep + 1) / fields.length) * 100)}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={((currentStep + 1) / fields.length) * 100} sx={{ height: 8, borderRadius: 4 }} />
            </Card>

            {!showReview ? (
                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                    <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
                        <Typography variant="h5" align="center" gutterBottom fontWeight="bold">{template.name || "Design Template"}</Typography>
                        <Box sx={{ mt: 4, mb: 4 }}><Typography variant="h6" gutterBottom>{labelEn} {isRequired && <span style={{ color: 'red' }}>*</span>}</Typography>{labelHi && <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{labelHi}</Typography>}{renderFieldInput()}</Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                            <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBack} sx={{ visibility: currentStep === 0 ? 'hidden' : 'visible' }}>Back</Button>
                            <Button variant="contained" color="primary" endIcon={currentStep === fields.length - 1 ? <CheckCircle /> : <ArrowForward />} onClick={handleNext} disabled={!isCurrentFieldValid()} sx={{ px: 4 }}>{currentStep === fields.length - 1 ? 'Review' : 'Save & Next'}</Button>
                        </Box>
                    </CardContent>
                </Card>
            ) : (
                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                    <Box sx={{ bgcolor: 'success.main', color: 'white', p: 2, display: 'flex', alignItems: 'center' }}><CheckCircle sx={{ mr: 1 }} /><Typography variant="h6">Review Your Information</Typography></Box>
                    <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
                        <Box sx={{ bgcolor: '#f8f9fa', p: 2, borderRadius: 2, mb: 3 }}><Typography variant="subtitle2" color="text.secondary">Template Name</Typography><Typography variant="body1" fontWeight="bold">{template.name || 'N/A'}</Typography></Box>
                        <Typography variant="h6" gutterBottom>📝 Your Details</Typography>
                        {fields.map((field, idx) => {
                            const fId = field.field_name || field.key || field.id; const val = formData[fId]; const isImg = val && typeof val === 'string' && (val.startsWith('http') || val.startsWith('data:image'));
                            return (
                                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, mb: 1, bgcolor: '#f8f9fa', borderRadius: 2, '&:hover': { bgcolor: '#e9ecef' } }}>
                                    <Box sx={{ flex: 1 }}><Typography variant="body2" fontWeight="bold">{field.labels?.en || field.label}</Typography>{isImg ? <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}><img src={val} alt="thumb" style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover', marginRight: 8 }} /><Typography variant="caption" color="success.main">Image Uploaded</Typography></Box> : <Typography variant="body2" color="text.secondary">{val || '(Not provided)'}</Typography>}</Box>
                                    <IconButton size="small" color="primary" onClick={() => { setCurrentStep(idx); setShowReview(false); }}><Edit fontSize="small" /></IconButton>
                                </Box>
                            );
                        })}
                        <Alert severity="info" sx={{ mt: 3, mb: 3 }}>After submitting, you will be redirected to the download page.</Alert>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Button variant="outlined" onClick={() => { setCurrentStep(0); setShowReview(false); }}>Edit Information</Button>
                            <Button variant="contained" color="success" size="large" onClick={submitForm} disabled={isSubmitting}>{isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Submit'}</Button>
                        </Box>
                    </CardContent>
                </Card>
            )}

            <Modal open={uploadModalOpen} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ bgcolor: 'white', p: 4, borderRadius: 3, maxWidth: 400, width: '90%', textAlign: 'center', outline: 'none' }}>
                    {!uploadSuccessUrl ? <><CircularProgress variant="determinate" value={uploadProgress} size={60} thickness={4} /><Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Uploading...</Typography><LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 10, borderRadius: 5, mt: 1 }} /></> : <><CheckCircle color="success" sx={{ fontSize: 60 }} /><Typography variant="h5" sx={{ mt: 2, mb: 1 }}>Upload Complete!</Typography><Button fullWidth variant="contained" onClick={() => setUploadModalOpen(false)}>OK</Button></>}
                </Box>
            </Modal>
        </Container>
    );
};

export default SmartForm;